import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { MdChevronLeft, MdChevronRight, MdExpandMore } from "react-icons/md";
import Button, { IconButton } from "components/button";
import { SearchInput } from "components/fields";
import { SegmentedControl } from "components/tabs";
import MiniCalendar from "components/calendar/MiniCalendar";

const VIEW_OPTIONS = [
  { value: "timeGridDay", label: "Day" },
  { value: "timeGridWeek", label: "Week" },
  { value: "dayGridMonth", label: "Month" },
];

/**
 * Calendar toolbar: visible range (opens a month picker), prev/today/next,
 * view switcher and service search.
 */
const CalendarHeader = ({
  title,
  activeDate,
  currentView,
  onViewChange,
  onPrev,
  onNext,
  onToday,
  onDateSelect,
  searchTerm,
  onSearchChange,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  const isCurrentMonth = title?.toLowerCase().includes(dayjs().format("MMMM").toLowerCase());

  useEffect(() => {
    if (!pickerOpen) return undefined;

    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setPickerOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setPickerOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [pickerOpen]);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative" ref={pickerRef}>
        <Button
          variant="ghost"
          rightIcon={<MdExpandMore aria-hidden="true" className="h-5 w-5" />}
          onClick={() => setPickerOpen((open) => !open)}
          aria-expanded={pickerOpen}
          aria-haspopup="dialog"
          className="-ml-3 text-lg font-bold"
        >
          {title}
        </Button>
        {pickerOpen && (
          <div className="absolute left-0 top-full z-30 mt-2 w-[340px] max-w-[calc(100vw_-_2rem)]">
            <MiniCalendar
              value={activeDate}
              onChange={(date) => {
                onDateSelect(date);
                setPickerOpen(false);
              }}
            />
          </div>
        )}
      </div>

      <div className="flex items-center gap-1">
        <IconButton
          icon={<MdChevronLeft />}
          label="Previous"
          variant="secondary"
          size="sm"
          onClick={onPrev}
        />
        <Button variant="secondary" size="sm" onClick={onToday} disabled={isCurrentMonth}>
          Today
        </Button>
        <IconButton
          icon={<MdChevronRight />}
          label="Next"
          variant="secondary"
          size="sm"
          onClick={onNext}
        />
      </div>

      <SegmentedControl
        aria-label="Calendar view"
        options={VIEW_OPTIONS}
        value={currentView}
        onChange={onViewChange}
      />

      <SearchInput
        value={searchTerm}
        onChange={onSearchChange}
        delay={500}
        placeholder="Search services…"
        ariaLabel="Search services"
        className="w-full sm:ml-auto sm:w-64"
      />
    </div>
  );
};

export default CalendarHeader;
